import { useMemo, useRef } from 'react';
import { GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';

interface IStableGridModelsParams {
  page: number;
  limit: number;
  sort_by?: string;
  sort_order?: string;
  totalCount: number;
  loading: boolean;
}

interface IStableGridModels {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  rowCount: number;
}

/**
 * Server-side DataGrid tables need referentially stable controlled models.
 *
 * The DataGrid treats `sortModel`/`paginationModel` as controlled props and, on
 * every reference change, runs its internal apply-effect. For `sortModel` that
 * re-fires `onSortModelChange`, which writes back to the filters store on each
 * render and clobbers the page the user just navigated to. Memoizing keeps the
 * reference stable across unrelated re-renders (e.g. page changes).
 *
 * `rowCount` is additionally frozen to its last known value while a page is
 * loading, so the grid never briefly sees a smaller/default count, clamps the
 * current page as out-of-range, and reverts pagination to the previous page.
 */
export const useStableGridModels = ({
  page,
  limit,
  sort_by,
  sort_order,
  totalCount,
  loading,
}: IStableGridModelsParams): IStableGridModels => {
  const paginationModel = useMemo(
    () => ({ page: page - 1, pageSize: limit }),
    [page, limit],
  );

  const sortModel = useMemo<GridSortModel>(
    () =>
      sort_by ? [{ field: sort_by, sort: sort_order as 'asc' | 'desc' }] : [],
    [sort_by, sort_order],
  );

  const rowCountRef = useRef(totalCount);
  const rowCount = useMemo(() => {
    if (!loading) {
      rowCountRef.current = totalCount;
    }
    return rowCountRef.current;
  }, [loading, totalCount]);

  return { paginationModel, sortModel, rowCount };
};
